import { useEffect, useMemo, useState } from 'react';
import {
  TeButton,
  TeCell,
  TeCheckbox,
  TeContainer,
  TeContainerSlim,
  TeModal,
  TeRow,
  TeTable,
  TeText,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';
import { styled } from 'styled-components';

import { useMigrationCandidateUsers } from '../../hooks/queries/migration.queries';
import {
  useAcceptMigrationCandidateUser,
  useRejectMigrationCandidateUser,
} from '../../hooks/queries/migration.mutations';
import { useErrorDrawer } from '../../providers/ErrorDrawerContext/error-drawer.context';
import { deriveNameFromEmail } from '../../utils/deriveNameFromEmail';

interface Props {
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
}

/**
 * Review-suggested-users modal (Suggested User / migration candidate user
 * vertical slice — see SPEC.md).
 *
 * The PM `migrationCandidateUserBody` supplies neither `name` nor `source`,
 * so rows are email-only: the display name is derived from the email
 * local-part and the Source column is omitted entirely (SPEC Q3b).
 *
 * Selection is opt-in: the modal opens with **no** rows selected, and a row's
 * checkbox toggles whether it is included in the next submit (reversible,
 * local-only). This reverses SPEC Q7's original select-all-by-default
 * design, which modelled the state as an opt-out `uncheckedIds` list —
 * testers wanted nothing selected on open instead.
 *
 * Select All / Deselect All is a single tri-state checkbox in the table
 * header (mirroring `UsersTable`): unchecked selects nothing, checked
 * selects every visible row, and it renders indeterminate while some but not
 * all rows are checked.
 *
 * The trash button is a separate, independent per-row action: it calls the
 * real `reject` mutation for that row — an irreversible, immediate
 * server-side action with no confirm step. The row disappears once the
 * mutation's `useMigrationCandidateUsers` list invalidation refetches.
 *
 * There is no bulk-accept endpoint, so submitting fires one `accept` call per
 * checked row and consumes the results as they resolve (mirroring
 * `InvitationModal`'s `for await ... map` fan-out — SPEC Q6). On completion,
 * an error toast fires if any row failed (SPEC Q8) — no per-row notifications.
 */
function SuggestedUsersModal({ open, onClose }: Props) {
  const { t } = useTranslation('users');
  const notificationService = useNotification();
  const { showError } = useErrorDrawer();
  const { data } = useMigrationCandidateUsers();
  const { mutateAsync: acceptMigrationCandidateUser } = useAcceptMigrationCandidateUser();
  const { mutate: rejectMigrationCandidateUser } = useRejectMigrationCandidateUser();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rejectingIds, setRejectingIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Defensive: the list endpoint documents itself as pending-only, but this
  // guards against an unexpected accepted/rejected item ever reaching review.
  const visibleCandidates = useMemo(
    () => (data?.items ?? []).filter(({ status }) => status === 'pending'),
    [data]
  );
  const checkedCandidates = useMemo(
    () => visibleCandidates.filter(({ id }) => selectedIds.includes(id)),
    [visibleCandidates, selectedIds]
  );

  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setRejectingIds([]);
    }
  }, [open]);

  const isAllSelected =
    visibleCandidates.length > 0 && selectedIds.length === visibleCandidates.length;
  const isIndeterminate = selectedIds.length > 0 && !isAllSelected;

  const toggleChecked = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    setSelectedIds(isAllSelected ? [] : visibleCandidates.map(({ id }) => id));
  };

  const rejectCandidate = (id: string) => {
    setRejectingIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    rejectMigrationCandidateUser(id, {
      onSettled: () => {
        setRejectingIds((prev) => prev.filter((rejectingId) => rejectingId !== id));
      },
      onError: (error: unknown) => {
        if (!showError(error)) {
          notificationService.addNotification(
            t('suggestedUsersNotification.rejectErrorTitle'),
            t('suggestedUsersNotification.rejectErrorDescription'),
            'error'
          );
        }
      },
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    let successCount = 0;
    let failedCount = 0;

    for await (const outcome of checkedCandidates.map((candidate) =>
      acceptMigrationCandidateUser(candidate.id)
        .then(() => 'success' as const)
        .catch((error: unknown) => {
          if (showError(error)) {
            return 'handled' as const;
          }
          return 'failed' as const;
        })
    )) {
      if (outcome === 'success') successCount += 1;
      else if (outcome === 'failed') failedCount += 1;
    }

    setIsSubmitting(false);

    if (failedCount > 0) {
      notificationService.addNotification(
        t('suggestedUsersNotification.errorTitle'),
        t('suggestedUsersNotification.errorDescription'),
        'error'
      );
    }

    onClose(successCount > 0);
  };

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={() => onClose(false)}
      modalTitle={t('suggestedUsersModal.title')}
      contentPadding={0}
      closeable
    >
      <TeContainer backgroundColor="background.primary" width={640} padding={['m', 's']} fill>
        <TeContainerSlim gap="m" column grow>
          <TeText color="text.secondary">
            {t('suggestedUsersModal.subtitle', { count: visibleCandidates.length })}
          </TeText>

          <TeTable size="s" evenOdd>
            <TeRow>
              <TeCell justifyContent="center" noPadding compact>
                <TeCheckbox
                  checked={isIndeterminate || isAllSelected}
                  indeterminate={isIndeterminate}
                  techange={toggleAll}
                  wrapperSize={40}
                />
              </TeCell>
              <TeCell>
                <TeText weight={700} size={14}>
                  {t('suggestedUsersModal.name')}
                </TeText>
              </TeCell>
              <TeCell>
                <TeText weight={700} size={14}>
                  {t('suggestedUsersModal.email')}
                </TeText>
              </TeCell>
              <TeCell compact />
            </TeRow>

            {visibleCandidates.map(({ id, email }) => (
              <TeRow key={id}>
                <TeCell justifyContent="center" noPadding compact>
                  <TeCheckbox
                    checked={selectedIds.includes(id)}
                    techange={() => toggleChecked(id)}
                    wrapperSize={40}
                  />
                </TeCell>
                <TeCell>
                  <TruncatedCell $maxWidth={160}>
                    <TeText size={12} truncate>
                      {deriveNameFromEmail(email)}
                    </TeText>
                  </TruncatedCell>
                </TeCell>
                <TeCell>
                  <TruncatedCell $maxWidth={280}>
                    <TeText size={12} truncate>
                      {email}
                    </TeText>
                  </TruncatedCell>
                </TeCell>
                <TeCell justifyContent="center" compact>
                  <TeButton
                    icon="delete"
                    variant="neutral"
                    backgroundColor="transparent"
                    iconColor="icon.secondary"
                    size="xs"
                    title={t('suggestedUsersModal.remove')}
                    loading={rejectingIds.includes(id)}
                    disabled={rejectingIds.includes(id)}
                    teclick={() => rejectCandidate(id)}
                  />
                </TeCell>
              </TeRow>
            ))}
          </TeTable>
        </TeContainerSlim>
      </TeContainer>

      <TeButton
        slot="secondary"
        text={t('cancel')}
        variant="neutral"
        teclick={() => onClose(false)}
        size="s"
      />
      <TeButton
        slot="primary"
        text={t('suggestedUsersModal.submit')}
        variant="primary"
        teclick={handleSubmit}
        loading={isSubmitting}
        disabled={isSubmitting || checkedCandidates.length === 0}
        size="s"
      />
    </TeModal>
  );
}

export default SuggestedUsersModal;

/**
 * Caps a table cell's slotted content at a fixed pixel width so the Name and
 * Email columns can never grow wide enough to force horizontal scroll on the
 * table/modal (e.g. an implausibly long email local-part). `overflow: hidden`
 * doubles as the flex-item escape hatch here: `.cell` (inside `TeCell`'s
 * shadow root) is a flex container, and a nowrap-text flex item's automatic
 * minimum width is otherwise its full unbroken content width — which would
 * override `max-width` and defeat the cap. `min-width: 0` reinforces that
 * across engines. Paired with `TeText truncate` for the ellipsis + native
 * title tooltip on the full value.
 */
const TruncatedCell = styled.div<{ $maxWidth: number }>`
  min-width: 0;
  max-width: ${({ $maxWidth }) => $maxWidth}px;
  overflow: hidden;
`;
